import React, { useState, useRef, useEffect, useMemo } from "react";
import { motion, AnimatePresence, Variants } from "framer-motion";
import Logo from "../../assets/img/logo.webp";
import FollowAs from "../footer/FollowAs";
import walletClose from "../../assets/img/pinkSclose.png";
import walletOpen from "../../assets/img/pinkSopen.png";

/** ⏰ Date de fin du presale */
const SALE_END_DATE_UTC = new Date("2025-07-22T14:00:00Z");

/* ---------- Hook Countdown ---------- */
const useCountdown = (targetDate: Date) => {
  const [timeLeft, setTimeLeft] = useState(
    () => targetDate.getTime() - Date.now()
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setTimeLeft(() => {
        const remaining = targetDate.getTime() - Date.now();
        return remaining >= 0 ? remaining : 0;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [targetDate]);

  const seconds = Math.floor((timeLeft / 1000) % 60);
  const minutes = Math.floor((timeLeft / 1000 / 60) % 60);
  const hours = Math.floor((timeLeft / 1000 / 60 / 60) % 24);
  const days = Math.floor(timeLeft / 1000 / 60 / 60 / 24);

  return { days, hours, minutes, seconds, isFinished: timeLeft <= 0 };
};

/* ---------- Animations ---------- */
const sidebarVariants: Variants = {
  open: {
    x: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 260,
      damping: 20,
      staggerChildren: 0.1,
    },
  },
  closed: {
    x: "100%",
    opacity: 0,
    transition: { type: "spring", stiffness: 300, damping: 30 },
  },
};

const itemVariants: Variants = {
  open: {
    y: 0,
    opacity: 1,
    transition: { y: { stiffness: 1000, velocity: -100 } },
  },
  closed: { y: 20, opacity: 0, transition: { y: { stiffness: 1000 } } },
};

/* ---------- composant ---------- */
const BuyPresaleSidebar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);

  const targetDate = useMemo(() => SALE_END_DATE_UTC, []);
  const { days, hours, minutes, seconds, isFinished } =
    useCountdown(targetDate);

  /* Fermer si clic extérieur ou événement global */
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (sidebarRef.current && !sidebarRef.current.contains(e.target as Node))
        setIsOpen(false);
    };
    const handleGlobalClose = () => setIsOpen(false);

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("closeAllSidebars", handleGlobalClose);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("closeAllSidebars", handleGlobalClose);
    };
  }, []);

  const handleToggle = () => {
    window.dispatchEvent(new Event("closeAllSidebars"));
    setIsOpen((prev) => !prev);
  };

  /* Petite animation “pop” chaque seconde : on change la clé du <motion.span> */
  const countdownKey = `${days}-${hours}-${minutes}-${seconds}`;

  return (
    <>
      {/* Bouton toggle */}
      <button
        onClick={handleToggle}
        aria-label="Join the Presale on PinkSale"
        className="relative flex items-center group"
      >
        <img
          src={walletClose}
          alt="Closed wallet icon"
          className="w-8 h-8 transition-all duration-500 ease-in-out group-hover:opacity-0 group-hover:scale-110 mb-4"
        />
        <img
          src={walletOpen}
          alt="Open wallet icon"
          className="absolute w-8 h-8 opacity-0 group-hover:opacity-100 scale-110 rotate-6 transition-all duration-500 ease-in-out"
        />
        <p className="ml-1 text-base sm:text-[16px] font-extrabold text-red-200 relative group-hover:text-[var(--color-primary)] transition-colors duration-300 after:absolute after:bottom-[-4px] after:left-0 after:w-0 group-hover:after:w-full after:h-[2px] after:bg-[var(--color-primary)] after:transition-all after:duration-500 after:ease-in-out">
          Presale on PINKSALE
        </p>
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.aside
            ref={sidebarRef}
            initial="closed"
            animate="open"
            exit="closed"
            variants={sidebarVariants}
            className="absolute top-20 right-0 z-40 w-90 bg-[#031B34] text-white shadow-2xl overflow-hidden"
          >
            <div className="flex flex-col h-full p-6">
              {/* Header */}
              <div className="flex items-center justify-between">
                <motion.div variants={itemVariants} className="pb-4">
                  <h3 className="text-2xl font-bold">Presale</h3>
                  <p className="text-sm text-white/60">UC Waves</p>

                  {/* Compteur animé */}
                  <br />
                  <br />
                  <p>Presale Ends In</p>
                  <motion.div
                    key={countdownKey}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ type: "spring", stiffness: 400, damping: 20 }}
                    className="mt-2 text-xs sm:text-sm font-semibold text-white/80"
                  >
                    {isFinished
                      ? "Presale finished"
                      : `${days}d ${hours}h ${minutes}m ${seconds}s`}
                  </motion.div>
                </motion.div>

                <img src={Logo} alt="logoUCW" width={100} />
              </div>

              <motion.div
                variants={itemVariants}
                className="mt-6 flex-1 flex flex-col items-center justify-center space-y-4"
              >
                <motion.a
                  href="https://www.pinksale.finance/launchpad/base/0xc5bB4ADc597310aae57fa1C2FF36e632D43ae243"
                  target="_blank"
                  rel="noopener noreferrer"
                  variants={itemVariants}
                  className="px-6 py-2 bg-[#f10303] hover:bg-[#ff6666] text-white text-sm font-semibold rounded-full shadow-lg transition-colors duration-300"
                >
                  Presale on PinkSale
                </motion.a>

                <motion.div variants={itemVariants}>
                  <FollowAs />
                </motion.div>
              </motion.div>

              {/* Footer */}
              <motion.div
                variants={itemVariants}
                className="mt-auto text-center text-xs text-white/40 pb-4"
              >
                Powered by NextGenWaves
              </motion.div>
            </div>
          </motion.aside>
        )}
      </AnimatePresence>
    </>
  );
};

export default BuyPresaleSidebar;
